import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import BusinessList from "../Components/BusinessList";
import ReviewList from "../Components/ReviewList";
import RatingTrendChart from "../Components/RatingTrendChart";
import CompareView from "../Components/CompareView";
import FranchiseView from "../Components/FranchiseView";
import { useAuth } from "../context/AuthContext";
import RiraLogo from "../Components/RiraLogo";
import { fetchPlaceSuggestions } from "../utils/placesAutocomplete";

const KW_STOP = new Set([
  "the","a","an","and","or","but","in","on","at","to","for","of","with",
  "is","was","are","were","be","been","has","have","had","do","did","not",
  "it","its","this","that","they","their","very","so","my","we","i","he",
  "she","you","our","all","from","by","as","up","out","about","over","than",
  "me","us","him","her","who","what","when","where","how","just","too","also",
  "would","could","should","will","really","even","much","more","most","well",
  "got","get","no","yes","if","am","every","any","some","there","here",
]);


function KeywordTrend({ reviews }) {
  const trends = useMemo(() => {
    if (!reviews || reviews.length === 0) return [];
    const now = new Date();
    const ty = now.getFullYear(), tm = now.getMonth();
    const ly = tm === 0 ? ty - 1 : ty, lm = tm === 0 ? 11 : tm - 1;
    const countWords = (revs) => {
      const freq = {};
      revs.forEach((r) => {
        if (!r.comment) return;
        r.comment.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).forEach((w) => {
          if (w.length > 3 && !KW_STOP.has(w)) freq[w] = (freq[w] || 0) + 1;
        });
      });
      return freq;
    };
    const thisRevs = reviews.filter((r) => { if (!r.date) return false; const d = new Date(r.date); return d.getFullYear() === ty && d.getMonth() === tm; });
    const lastRevs = reviews.filter((r) => { if (!r.date) return false; const d = new Date(r.date); return d.getFullYear() === ly && d.getMonth() === lm; });
    if (thisRevs.length === 0) return [];
    const thisFreq = countWords(thisRevs);
    const lastFreq = countWords(lastRevs);
    return Object.entries(thisFreq).filter(([, c]) => c >= 2).sort(([, a], [, b]) => b - a).slice(0, 8).map(([word, count]) => {
      const prev = lastFreq[word] || 0;
      const change = prev > 0 ? Math.round(((count - prev) / prev) * 100) : null;
      return { word, count, change };
    });
  }, [reviews]);

  if (trends.length === 0) return null;
  return (
    <div className="rm-kw-trend">
      <div className="rm-kw-trend-title">Keyword Trend — This Month</div>
      <div className="rm-kw-trend-chips">
        {trends.map(({ word, count, change }) => (
          <div key={word} className="rm-kw-trend-chip">
            <span className="rm-kw-trend-word">{word}</span>
            <span className="rm-kw-trend-count">{count}</span>
            {change !== null && (
              <span className={`rm-kw-trend-delta ${change >= 0 ? "up" : "down"}`}>
                {change >= 0 ? "↑" : "↓"}{Math.abs(change)}%
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

function TopInsightsPanel({ reviews, token, businessName }) {
  const [insights, setInsights] = useState(null);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (!reviews || reviews.length < 4 || !token) return;
    setInsights(null);
    setLoading(true);
    fetch(`${API_BASE}/insights/top`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body:    JSON.stringify({ reviews: reviews.slice(0, 60), cache_key: businessName || "" }),
    })
      .then((r) => {
        if (!r.ok) { console.warn("insights/top error:", r.status); setLoading(false); return null; }
        return r.json();
      })
      .then((d) => { if (d) { setInsights(d); } setLoading(false); })
      .catch((e) => { console.warn("insights/top failed:", e); setLoading(false); });
  }, [reviews, token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="ti-panel">
      <div className="ti-header">Top Insights <span className="ti-loading">Analysing reviews…</span></div>
      <div className="ti-cols">
        {["bad","good","req"].map((t) => (
          <div key={t} className="ti-col">
            <div className={`ti-col-title ti-col-title--${t}`}>&nbsp;</div>
            <div className="ti-chips">
              {[1,2,3].map((n) => <span key={n} className="ti-chip-skeleton" />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (!insights) return null;

  const sections = [
    { key: "complaints", title: "Complaints", cls: "bad"  },
    { key: "positives",  title: "Positives",  cls: "good" },
    { key: "requests",   title: "Requests",   cls: "req"  },
  ].filter(({ key }) => insights[key]?.length > 0);

  if (sections.length === 0) return null;

  return (
    <div className="ti-panel">
      <div className="ti-header">Top Insights <span className="ti-powered">AI</span></div>
      <div className={`ti-cols ti-cols--${sections.length}`}>
        {sections.map(({ key, title, cls }) => (
          <div key={key} className="ti-col">
            <div className={`ti-col-title ti-col-title--${cls}`}>{title}</div>
            <div className="ti-chips">
              {insights[key].map((text, i) => (
                <span key={i} className={`ti-chip ti-chip--${cls}`}>{text}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StarBreakdown({ reviews }) {
  const counts = useMemo(() => {
    const c = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    (reviews || []).forEach((r) => { if (c[r.rating] !== undefined) c[r.rating]++; });
    return c;
  }, [reviews]);
  const max = Math.max(...Object.values(counts), 1);
  return (
    <div className="rm-star-breakdown">
      {[5, 4, 3, 2, 1].map((star) => {
        const pct      = (counts[star] / max) * 100;
        const colorCls = star >= 4 ? "good" : star === 3 ? "avg" : "bad";
        return (
          <div key={star} className="rm-star-row">
            <span className="rm-star-label">{star}★</span>
            <div className="rm-bar-track">
              <div className={`rm-bar-fill ${colorCls}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="rm-star-count">{counts[star]}</span>
          </div>
        );
      })}
    </div>
  );
}

function CenterSearch({ onSubmit, onCancel }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = useCallback(async (value) => {
    const list = await fetchPlaceSuggestions(value);
    setSuggestions(list); setShowDrop(list.length > 0); setActiveIdx(-1);
  }, []);

  const handleChange = (e) => {
    setQuery(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 150);
  };

  const handleSelect = (s) => {
    const display = s.main_text || s.description;
    const search  = s.description || s.main_text;
    setQuery(display); setSuggestions([]); setShowDrop(false);
    onSubmit(search);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSuggestions([]); setShowDrop(false);
    onSubmit(query.trim());
  };

  const handleKeyDown = (e) => {
    if (!showDrop) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); handleSelect(suggestions[activeIdx]); }
    else if (e.key === "Escape") { setShowDrop(false); onCancel(); }
  };

  return (
    <div className="cs-wrap">
      <div className="cs-title">Search any business</div>
      <div className="cs-search-outer" ref={wrapRef}>
        <form className="cs-form" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            className="cs-input"
            type="text"
            placeholder="e.g. SR Jungle Resort, Starbucks New York..."
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (query.trim().length >= 2) fetchSuggestions(query); }}
            autoComplete="off"
          />
          <button type="submit" className="cs-btn">Search</button>
        </form>
        {showDrop && suggestions.length > 0 && (
          <ul className="cs-ac-list">
            {suggestions.map((s, i) => (
              <li key={s.place_id || i} className={`cs-ac-item ${i === activeIdx ? "active" : ""}`} onMouseDown={() => handleSelect(s)}>
                <span className="cs-ac-main">{s.main_text}</span>
                {s.secondary_text && <span className="cs-ac-sub">{s.secondary_text}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <button className="cs-cancel" onClick={onCancel}>Cancel</button>
    </div>
  );
}

function CenterInsights() {
  return (
    <div className="ci-wrap">
      <div className="ci-cards">
        <div className="ci-card">
          <div className="ci-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </div>
          <div className="ci-card-title">Sentiment Analysis</div>
          <div className="ci-card-desc">Instantly see positive vs negative review trends for any business</div>
        </div>
        <div className="ci-card">
          <div className="ci-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div className="ci-card-title">AI Reply Drafts</div>
          <div className="ci-card-desc">Generate professional replies in seconds with tone control</div>
        </div>
        <div className="ci-card">
          <div className="ci-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
            </svg>
          </div>
          <div className="ci-card-title">Rating Trends</div>
          <div className="ci-card-desc">Track how ratings change over time with visual charts</div>
        </div>
        <div className="ci-card">
          <div className="ci-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="ci-card-title">Competitor Benchmarking</div>
          <div className="ci-card-desc">Compare your reviews side-by-side against competitors</div>
        </div>
      </div>
      <div className="ci-stat-row">
        <div className="ci-stat"><span className="ci-stat-num">35%</span><span className="ci-stat-label">more trust when you reply to reviews</span></div>
        <div className="ci-stat"><span className="ci-stat-num">4.5★</span><span className="ci-stat-label">average rating drives 28% more clicks</span></div>
        <div className="ci-stat"><span className="ci-stat-num">72%</span><span className="ci-stat-label">of customers read replies before visiting</span></div>
      </div>
    </div>
  );
}

const DEMO_REVIEWS = [
  { author: "Sarah M.", rating: 5, comment: "Absolutely fantastic experience! The staff was incredibly welcoming and the service exceeded all expectations. Will definitely be returning!", date: "2026-04-20", has_reply: true },
  { author: "James K.", rating: 4, comment: "Great place overall. Food was delicious and the ambiance was perfect. Minor issue was the wait time, but staff was very apologetic about it.", date: "2026-04-15", has_reply: false },
  { author: "Priya R.", rating: 2, comment: "Disappointed with my visit. Service was slow and the order was incorrect. Not up to the usual standard I've come to expect.", date: "2026-04-10", has_reply: false },
  { author: "Tom W.", rating: 5, comment: "Best experience I've had in years! Everything was perfect from start to finish. Highly recommend to anyone looking for quality service.", date: "2026-04-08", has_reply: true },
  { author: "Emily C.", rating: 3, comment: "Average experience this time around. Some things were good, others not so much. Maybe just an off day for the team.", date: "2026-04-05", has_reply: false },
  { author: "Marcus L.", rating: 5, comment: "Outstanding! The attention to detail here is remarkable. Every aspect of our visit was handled with professionalism and care.", date: "2026-03-28", has_reply: true },
];

function DemoPage({ onConnect }) {
  return (
    <div className="demo-page-wrap">
      <div className="demo-connect-banner">
        <div className="demo-connect-left">
          <div className="demo-connect-text">
            <div className="demo-connect-title">Connect your Google Business Profile</div>
            <div className="demo-connect-sub">See your real reviews, reply to customers, and get AI-powered insights</div>
          </div>
        </div>
        <button className="demo-connect-btn" onClick={onConnect}>Connect Google Business</button>
      </div>
      <div className="rm-biz-header">
        <div className="rm-biz-header-top">
          <h2>Sample Business <span className="demo-badge">Demo</span></h2>
          <div className="rm-biz-badges">
            <div className="rm-rating-badge good">★ 4.0 / 5</div>
            <div className="rm-response-rate good" title="Reply response rate">50% replied</div>
          </div>
        </div>
        <StarBreakdown reviews={DEMO_REVIEWS} />
      </div>
      <ReviewList
        reviews={DEMO_REVIEWS}
        loading={false}
        businessName="Sample Business"
        googleConnected={false}
      />
    </div>
  );
}

function Dashboard() {
  const { user, token, logout, completeAuth } = useAuth();
  const navigate                        = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view                            = searchParams.get("view") || "";
  const compareMode                     = view === "compare";
  const franchiseMode                   = view === "franchise";
  const showSearch                      = view === "search";
  const setView                         = (v) => setSearchParams(v ? { view: v } : {});
  const bizKey    = `rm_businesses_${user?.id}`;

  const contentRef = useRef(null);
  useEffect(() => {
    // Reset every possible scroll container — needed for iOS/mobile
    if (contentRef.current) contentRef.current.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    try { window.scroll({ top: 0, left: 0, behavior: "instant" }); } catch (_) { window.scrollTo(0, 0); }
  }, [view]);

  const [reviews,         setReviews]         = useState([]);
  const [businessName,    setBusinessName]    = useState("");
  const [realData,        setRealData]        = useState(false);
  const [savedBusinesses, setSavedBusinesses] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`rm_businesses_${user?.id}`) || "[]"); } catch { return []; }
  });
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState("");
  // eslint-disable-next-line no-unused-vars
  const [ratingSummary,   setRatingSummary]   = useState([]);
  const [sidebarOpen,     setSidebarOpen]     = useState(false);
  const [alertMap,        setAlertMap]        = useState(() => {
    try { return JSON.parse(localStorage.getItem(`rm_alerts_${user?.id}`) || "{}"); } catch { return {}; }
  });
  const [googleStatus,    setGoogleStatus]    = useState(null);
  const confettiFired = useRef(new Set());

  const [notifications, setNotifications] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`rm_notifications_${user?.id}`) || "[]"); } catch { return []; }
  });
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const [verifyBanner,  setVerifyBanner]  = useState(true);
  const [verifyInput,   setVerifyInput]   = useState("");
  const [verifyMsg,     setVerifyMsg]     = useState({ text: "", ok: false });
  const [verifyStep,    setVerifyStep]    = useState("prompt");
  const [verifyLoading, setVerifyLoading] = useState(false);

  const handleResendVerification = () => {
    setVerifyMsg({ text: "", ok: false });
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/auth/resend-verification`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setVerifyStep("enter");
        if (d.verification_code) setVerifyInput(d.verification_code);
        setVerifyMsg({ text: d.verification_code ? `Dev mode — code: ${d.verification_code}` : "Code sent to your email.", ok: true });
      })
      .catch(() => setVerifyMsg({ text: "Failed to resend. Try again.", ok: false }));
  };

  const handleSubmitVerifyCode = (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    setVerifyMsg({ text: "", ok: false });
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: user?.email, token: verifyInput.trim().toUpperCase() }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.user) { completeAuth(token, { ...user, email_verified: 1 }); setVerifyBanner(false); }
        else setVerifyMsg({ text: d.detail || "Invalid code.", ok: false });
        setVerifyLoading(false);
      })
      .catch(() => { setVerifyMsg({ text: "Failed. Try again.", ok: false }); setVerifyLoading(false); });
  };


  useEffect(() => {
    if (!notifOpen) return;
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [notifOpen]);

  useEffect(() => {
    if (!token) return;
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/auth/google/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setGoogleStatus)
      .catch(() => {});
  }, [token]);

  useEffect(() => { localStorage.setItem(bizKey, JSON.stringify(savedBusinesses)); }, [savedBusinesses, bizKey]);

  useEffect(() => {
    if (!googleStatus) return;
    if (googleStatus.connected && googleStatus.location_set && !businessName) {
      fetchReviews(googleStatus.location_name);
    }
  }, [googleStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReviews = (name) => {
    setBusinessName(name);
    setLoading(true);
    setError("");
    const isOwnBusiness =
      googleStatus?.connected &&
      googleStatus?.location_set &&
      googleStatus?.location_name?.toLowerCase() === name.trim().toLowerCase();
    const endpoint = isOwnBusiness
      ? `${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/google/reviews`
      : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/reviews/${encodeURIComponent(name)}`;
    fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data) => {
        const revs   = data.reviews || [];
        const makeId = (r) => `${name}|${r.author}|${r.date}|${r.rating}`;
        const seenIds = new Set(JSON.parse(localStorage.getItem(`rm_seen_ids_${user?.id}`) || "[]"));
        const hasBaseline = revs.some((r) => seenIds.has(makeId(r)));
        if (hasBaseline) {
          const fresh = revs.filter((r) => !seenIds.has(makeId(r)));
          if (fresh.length > 0) {
            setNotifications((prev) => {
              const prevIds  = new Set(prev.map((n) => n.id));
              const newItems = fresh.filter((r) => !prevIds.has(makeId(r))).map((r) => ({ id: makeId(r), businessName: name, author: r.author, rating: r.rating, comment: r.comment, date: r.date }));
              const updated  = [...newItems, ...prev].slice(0, 20);
              localStorage.setItem(`rm_notifications_${user?.id}`, JSON.stringify(updated));
              return updated;
            });
          }
        }
        localStorage.setItem(`rm_seen_ids_${user?.id}`, JSON.stringify([...new Set([...seenIds, ...revs.map(makeId)])]));
        const negPct = revs.length ? revs.filter((r) => r.rating <= 2).length / revs.length : 0;
        const avg    = revs.length ? revs.reduce((s, r) => s + r.rating, 0) / revs.length : 0;
        if (avg >= 4.5 && !confettiFired.current.has(name)) {
          confettiFired.current.add(name);
          confetti({ particleCount: 140, spread: 75, origin: { y: 0.55 }, colors: ["#6366f1", "#a5b4fc", "#34d399", "#fbbf24", "#f472b6"] });
        }
        setReviews(revs);
        setRealData(data.real_data || false);
        setAlertMap((prev) => { const next = { ...prev, [name]: negPct > 0.3 }; localStorage.setItem(`rm_alerts_${user?.id}`, JSON.stringify(next)); return next; });
        setLoading(false);
        // Save last search for this user
        localStorage.setItem(`rm_last_search_${user?.id}`, name);
      })
      .catch(() => { setError("Failed to fetch reviews. Please try again."); setReviews([]); setLoading(false); });
  };

  const handleSubmit = (inputName) => {
    const name = inputName.trim();
    if (!name) { setError("Please enter a business name."); return; }
    if (!savedBusinesses.includes(name)) setSavedBusinesses((prev) => [name, ...prev].slice(0, 5));
    fetchReviews(name);
    setSidebarOpen(false);
    setView("");
  };

  const handleDelete = (name) => {
    setSavedBusinesses((prev) => prev.filter((b) => b !== name));
    if (businessName === name) { setBusinessName(""); setReviews([]); }
  };

  const handleLogout = () => { logout(); navigate("/"); };
  const handleGoogleConnect = () => navigate("/settings");

  const [locSwitcher, setLocSwitcher] = useState(false);
  const [locations,   setLocations]   = useState([]);
  const [locLoading,  setLocLoading]  = useState(false);

  const openLocSwitcher = () => {
    setLocSwitcher(true);
    if (locations.length > 0) return;
    setLocLoading(true);
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/google/locations`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { setLocations(d.locations || []); setLocLoading(false); })
      .catch(() => setLocLoading(false));
  };

  const switchLocation = (loc) => {
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/google/select-location`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ account_id: loc.account_id, location_id: loc.location_id, location_name: loc.name }),
    })
      .then((r) => r.json())
      .then(() => { setGoogleStatus((s) => ({ ...s, location_name: loc.name })); setLocSwitcher(false); setBusinessName(""); setReviews([]); })
      .catch(() => {});
  };

  const avgRating    = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;
  const ratingCls    = avgRating >= 4 ? "good" : avgRating >= 3 ? "average" : "bad";
  const responseRate = reviews.length ? Math.round(reviews.filter((r) => r.has_reply).length / reviews.length * 100) : null;

  return (
    <div className="rm-app">
      <header className="rm-header">
        <button className="rm-mobile-menu-btn" onClick={() => setSidebarOpen((o) => !o)} aria-label="Open menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="3" y1="6"  x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div className="rm-brand" onClick={() => { setBusinessName(""); setReviews([]); setView(""); }} title="Home">
          <RiraLogo size={28} showTagline />
        </div>
        <div className="rm-header-user">
          <div className="rm-user-info">
            <span className="rm-user-name">{user?.name}</span>
            <span className="rm-user-plan">{user?.plan} plan</span>
          </div>

          {/* Notification bell */}
          <div className="rm-notif-wrap" ref={notifRef}>
            <button className="rm-notif-btn" onClick={() => setNotifOpen((o) => !o)} title="Notifications">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {notifications.length > 0 && <span className="rm-notif-badge">{notifications.length > 9 ? "9+" : notifications.length}</span>}
            </button>
            {notifOpen && (
              <div className="rm-notif-panel">
                <div className="rm-notif-panel-header">
                  <span className="rm-notif-panel-title">Notifications</span>
                  {notifications.length > 0 && (
                    <button className="rm-notif-clear" onClick={() => { setNotifications([]); localStorage.removeItem(`rm_notifications_${user?.id}`); setNotifOpen(false); }}>
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="rm-notif-empty">No new reviews</div>
                ) : (
                  <div className="rm-notif-list">
                    {notifications.map((n) => (
                      <div key={n.id} className={`rm-notif-item ${n.rating <= 2 ? "rm-notif-item--bad" : ""}`}>
                        <div className="rm-notif-item-top">
                          <span className="rm-notif-item-author">{n.author}</span>
                          <span className="rm-notif-item-stars">{"★".repeat(n.rating)}{"☆".repeat(5 - n.rating)}</span>
                        </div>
                        <div className="rm-notif-item-biz">{n.businessName}</div>
                        {n.comment && <div className="rm-notif-item-comment">{n.comment.length > 90 ? n.comment.slice(0, 90) + "…" : n.comment}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <button className="rm-settings-btn" onClick={() => navigate("/settings")}>Settings</button>
          <button className="rm-logout-btn" onClick={handleLogout}>Sign out</button>
        </div>
      </header>

      {/* Email verification banner */}
      {verifyBanner && user && !user.email_verified && (
        <div className="rm-verify-banner">
          <div className="rm-verify-banner-left">
            <span className="rm-verify-icon">✉</span>
            <div>
              <div className="rm-verify-title">Please verify your email</div>
              <div className="rm-verify-sub">Sent to <strong>{user.email}</strong></div>
            </div>
          </div>
          <div className="rm-verify-banner-right">
            {verifyStep === "prompt" ? (
              <>
                <button className="rm-verify-btn" onClick={() => { setVerifyStep("enter"); handleResendVerification(); }}>Enter code</button>
                <button className="rm-verify-dismiss" onClick={() => setVerifyBanner(false)}>✕</button>
              </>
            ) : (
              <form className="rm-verify-form" onSubmit={handleSubmitVerifyCode}>
                <input className="rm-verify-input" placeholder="6-char code" value={verifyInput} onChange={(e) => setVerifyInput(e.target.value.toUpperCase())} maxLength={6} autoFocus />
                <button type="submit" className="rm-verify-btn" disabled={verifyLoading || verifyInput.length < 6}>{verifyLoading ? "…" : "Verify"}</button>
                <button type="button" className="rm-verify-dismiss" onClick={() => setVerifyBanner(false)}>✕</button>
              </form>
            )}
          </div>
          {verifyMsg.text && <div className={`rm-verify-msg ${verifyMsg.ok ? "ok" : "err"}`}>{verifyMsg.text}</div>}
        </div>
      )}


      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 499,
            background: "rgba(15,10,40,0.45)",
            backdropFilter: "blur(2px)",
            cursor: "pointer",
          }}
        />
      )}

      <div className="rm-body">
        <aside className={`rm-sidebar ${sidebarOpen ? "rm-sidebar--open" : ""}`}>
          <button className="rm-sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>

          {/* Search */}
          <button className={`sb-compare-top-btn${view === "search" ? " active" : ""}`} onClick={() => { setBusinessName(""); setReviews([]); setRealData(false); setView("search"); setSidebarOpen(false); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            Search Business
          </button>

          {/* Compare */}
          <button className={`sb-compare-top-btn${compareMode ? " active" : ""}`} onClick={() => { setView("compare"); setSidebarOpen(false); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="8" height="18" rx="1.5"/><rect x="14" y="3" width="8" height="18" rx="1.5"/></svg>
            Compare Businesses
          </button>

          {/* Franchise */}
          <button className={`sb-compare-top-btn${franchiseMode ? " active" : ""}`} onClick={() => { setView("franchise"); setSidebarOpen(false); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            Franchise Tracker
          </button>

          <div className="sb-nav-divider" />

          {/* Location switcher — shown when GBP connected */}
          {googleStatus?.connected && googleStatus?.location_set && (
            <div className="sb-loc-panel">
              <div className="sb-loc-row">
                <div className="sb-loc-info">
                  <span className="sb-loc-label">Your Location</span>
                  <span className="sb-loc-name">{googleStatus.location_name}</span>
                </div>
                <button className="sb-loc-switch" onClick={openLocSwitcher} title="Switch location">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                  </svg>
                </button>
              </div>
              {locSwitcher && (
                <div className="sb-loc-dropdown">
                  {locLoading && <p className="sb-loc-loading">Loading locations...</p>}
                  {!locLoading && locations.map((loc) => (
                    <button key={`${loc.account_id}-${loc.location_id}`} className={`sb-loc-item ${loc.name === googleStatus.location_name ? "active" : ""}`} onClick={() => switchLocation(loc)}>
                      <span>{loc.name}</span>
                      {loc.name === googleStatus.location_name && <span className="sb-loc-check">✓</span>}
                    </button>
                  ))}
                  <button className="sb-loc-cancel" onClick={() => setLocSwitcher(false)}>Cancel</button>
                </div>
              )}
            </div>
          )}

          <BusinessList
            businesses={savedBusinesses}
            onSelect={(name) => { fetchReviews(name); setSidebarOpen(false); }}
            onDelete={handleDelete}
            onClearAll={() => { setSavedBusinesses([]); setBusinessName(""); setReviews([]); }}
            selected={businessName}
            alertMap={alertMap}
          />

        </aside>

        <main className="rm-content" ref={contentRef}>
          {compareMode ? (
            <CompareView onExit={() => setView("")} />
          ) : franchiseMode ? (
            <FranchiseView onExit={() => setView("")} />
          ) : (
            <>
              {error && <div className="rm-error">{error}</div>}

              {!loading && businessName && !realData && reviews.length > 0 && (
                <div className="rm-demo-banner">
                  <strong>Sample Data</strong> — live reviews are temporarily unavailable. Showing representative data for this business.
                </div>
              )}

              {loading && (
                <div className="rm-search-loader">
                  <div className="rm-search-bars">
                    {[0,1,2,3,4].map((i) => (
                      <div key={i} className="rm-search-bar" style={{ animationDelay: `${i * 0.11}s` }} />
                    ))}
                  </div>
                  <div className="rm-search-loader-name">{businessName}</div>
                  <div className="rm-search-loader-text">
                    Fetching reviews<span className="rm-loader-dots"><span>.</span><span>.</span><span>.</span></span>
                  </div>
                </div>
              )}

              {!loading && businessName && (
                <>
                  <div className="rm-biz-header">
                    <div className="rm-biz-header-top">
                      <h2>{businessName}</h2>
                      <div className="rm-biz-badges">
                        {avgRating && <div className={`rm-rating-badge ${ratingCls}`}>★ {avgRating} / 5</div>}
                        {responseRate !== null && (
                          <div className={`rm-response-rate ${responseRate >= 70 ? "good" : responseRate >= 40 ? "avg" : "low"}`} title="Reply response rate">
                            {responseRate}% replied
                          </div>
                        )}
                        {realData && <span className="rm-real-badge">Live Data</span>}
                      </div>
                    </div>
                    {reviews.length > 0 && <StarBreakdown reviews={reviews} />}
                  </div>
                  {reviews.length > 0 && <RatingTrendChart reviews={reviews} />}
                  {reviews.length > 0 && <TopInsightsPanel reviews={reviews} token={token} businessName={businessName} />}
                  {reviews.length > 0 && <KeywordTrend reviews={reviews} />}
                  <ReviewList
                    reviews={reviews}
                    loading={loading}
                    businessName={businessName}
                    googleConnected={!!(googleStatus?.connected && googleStatus?.location_set)}
                  />
                </>
              )}

              {!loading && !businessName && showSearch && (
                <>
                  <CenterSearch onSubmit={handleSubmit} onCancel={() => setView("")} />
                  <CenterInsights />
                </>
              )}

              {!loading && !businessName && !showSearch && googleStatus && !googleStatus.connected && (
                <DemoPage onConnect={handleGoogleConnect} />
              )}

              {!loading && !businessName && !showSearch && (!googleStatus || googleStatus.connected) && (
                <CenterInsights />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
